import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComponentProps } from 'react';

import styles from './ui.module.css';

interface FieldBase {
  label: string;
  hint?: React.ReactNode;
}

export type TextFieldProps = FieldBase & ComponentProps<'input'>;

/** A labelled single-line input. */
export function TextField({ label, hint, ...rest }: TextFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        className={styles.input}
        id={id}
        aria-describedby={hint === undefined ? undefined : hintId}
        {...rest}
      />
      {hint !== undefined && (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

/** A password input with a button that shows or hides what was typed. */
export function PasswordField({ label, hint, ...rest }: TextFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.passwordWrap}>
        <input
          className={styles.input}
          id={id}
          type={visible ? 'text' : 'password'}
          aria-describedby={hint === undefined ? undefined : hintId}
          {...rest}
        />
        <button
          type="button"
          className={styles.reveal}
          aria-pressed={visible}
          aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
          onClick={() => {
            setVisible((current) => !current);
          }}
        >
          <EyeIcon crossed={visible} />
        </button>
      </div>
      {hint !== undefined && (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

export type TextAreaProps = FieldBase & ComponentProps<'textarea'>;

/** A labelled multi-line input. */
export function TextArea({ label, ...rest }: TextAreaProps): React.JSX.Element {
  const id = useId();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <textarea className={styles.input} id={id} {...rest} />
    </div>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }): React.JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {crossed && <path d="M4 4l16 16" />}
    </svg>
  );
}
